import * as React from "react";
import formatDate from "dateformat";
import {
  ResponsiveContainer,
  LineChart,
  YAxis,
  XAxis,
  Line,
  Tooltip,
  Legend,
} from "recharts";
import { transparentize } from "polished";
import { TooltipContainerStyles } from "../../design-tokens/charts";
import { DarkTheme } from "../../design-tokens/media-queries";
import { Colors } from "../../design-tokens/colors";
import { AskAboutContext } from "../../../site-context/ask-about-context";

export interface DailyData {
  date: string;
  [key: string]: number | string | null | undefined;
}

interface DailyCasesChartMiniProps {
  data: DailyData[];
  benchmarkData?: DailyData[];
  dataKey: string;
  color: string;
  benchmarkColor?: string;
  name: string;
  benchmarkName?: string;
  size?: "large" | "small";
}

const DailyCasesChartMini: React.FC<DailyCasesChartMiniProps> = ({
  data,
  benchmarkData,
  dataKey,
  color,
  benchmarkColor = Colors.LightBlue,
  name,
  benchmarkName,
  size,
}) => {
  const {
    store: { isMobile },
  } = React.useContext(AskAboutContext);

  const firstDataIndex = data.findIndex((d: any) => d[dataKey]);
  const dataToPlot = data.slice(firstDataIndex, data.length);

  const mergedData = dataToPlot.map((d) => {
    const matchingDay = benchmarkData?.find((bd) => bd.date === d.date);
    return {
      ...d,
      benchmarkValue: matchingDay ? matchingDay[dataKey] : null,
    };
  });

  let height = benchmarkData ? 180 : 135;
  const sizeAddOn = isMobile ? 50 : 100;
  height = size === "large" ? height + sizeAddOn : height;

  return (
    <div
      css={{
        height,
        width: "100%",
        fontSize: 10,
        [DarkTheme]: { text: { fill: Colors.DarkModeWhite } },
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={mergedData}>
          <YAxis
            yAxisId={dataKey}
            domain={["dataMin", "dataMax"]}
            dataKey={dataKey}
            hide={true}
            padding={{ top: 0, bottom: 10 }}
          />

          <XAxis
            dataKey="date"
            height={20}
            tickLine={false}
            tickMargin={5}
            interval="preserveStartEnd"
            minTickGap={15}
            tickFormatter={(date) => formatDate(date, "mmm d")}
          />

          <Line
            yAxisId={dataKey}
            type="basis"
            dataKey={dataKey}
            name={name}
            stroke={color}
            strokeWidth={3}
            dot={false}
            connectNulls={true}
          />

          {benchmarkData && (
            <YAxis
              domain={["dataMin", "dataMax"]}
              yAxisId="benchmark"
              dataKey="benchmarkValue"
              hide={true}
              padding={{ top: 0, bottom: 10 }}
            />
          )}

          {benchmarkData && (
            <Line
              type="basis"
              yAxisId="benchmark"
              dataKey="benchmarkValue"
              name={benchmarkName}
              stroke={transparentize(0.3, benchmarkColor)}
              strokeWidth={3}
              strokeDasharray="2 3"
              dot={false}
              connectNulls={true}
            />
          )}

          <Tooltip
            labelFormatter={(date) => formatDate(date, "mmm d")}
            cursor={false}
            contentStyle={TooltipContainerStyles}
            itemStyle={{ paddingBottom: 0 }}
          />

          {benchmarkData && (
            <Legend
              wrapperStyle={{
                paddingTop: 20,
                fontSize: 12,
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DailyCasesChartMini;
